import {
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withFetch,
} from '@angular/common/http';
import {
  ApplicationConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { provideApiConfiguration } from 'libs/api-client/src/lib/api-configuration';
import { Observable, map } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

interface I18nItem {
  id: string;
  ko: string;
  en: string;
  ja: string;
}

/**
 * flat key (예: "LAYOUT.NAV.HOME") → 중첩 객체 (예: { LAYOUT: { NAV: { HOME: "홈" } } }) 변환
 */
function flatKeyToNestedObject(items: I18nItem[], langField: 'ko' | 'en' | 'ja'): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const item of items) {
    const keys = item.id.split('.');
    let current = result;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
        current[keys[i]] = {};
      }
      current = current[keys[i]] as Record<string, unknown>;
    }

    current[keys[keys.length - 1]] = item[langField];
  }

  return result;
}

/**
 * lang 코드(ko-KR, en-US, ja-JP) → DB 필드명(ko, en, ja) 매핑
 */
function langCodeToField(lang: string): 'ko' | 'en' | 'ja' {
  const map: Record<string, 'ko' | 'en' | 'ja'> = {
    'ko-KR': 'ko',
    'en-US': 'en',
    'ja-JP': 'ja',
    'ko': 'ko',
    'en': 'en',
    'ja': 'ja',
  };
  return map[lang] || 'ko';
}

/**
 * DB API 호출 기반 TranslateLoader
 * 앱 시작 시 GET /api/v1/i18n/client 로 전체 번역 데이터를 가져와
 * 언어별로 중첩 객체로 변환하여 @ngx-translate에 주입합니다.
 */
class DbTranslateLoader implements TranslateLoader {
  private http: HttpClient;
  private rootUrl: string;

  constructor(httpBackend: HttpBackend, rootUrl: string) {
    this.http = new HttpClient(httpBackend);
    this.rootUrl = rootUrl;
  }

  getTranslation(lang: string): Observable<any> {
    const field = langCodeToField(lang);

    return this.http
      .get<I18nItem[]>(`${this.rootUrl}/api/v1/i18n/client`)
      .pipe(map(items => flatKeyToNestedObject(items, field)));
  }
}

export function DbTranslateLoaderFactory(httpBackend: HttpBackend) {
  return new DbTranslateLoader(httpBackend, environment.rootUrl);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      appRoutes,
      withViewTransitions(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      })
    ),
    provideHttpClient(withFetch()),
    provideApiConfiguration(environment.rootUrl),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: DbTranslateLoaderFactory,
        deps: [HttpBackend],
      },
      fallbackLang: 'ko-KR',
      lang: 'ko-KR',
    }),
  ],
};

